import React, { useState, useRef, useEffect, useCallback } from 'react';
import Config from 'src/config';
import { useDispatch, useSelector } from 'react-redux';
import useAuth from 'src/hooks/useAuth';
import ChatInternoMessageItem from './ChatInternoMessageItem';
import { Button } from 'src/@/components/ui/button';
import Iconify from 'src/components/Iconify';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import { setMessageDeleted } from 'src/redux/slices/staff';
import ModalLightbox from 'src/@/components/shared/ModalLightbox';
// actions
import v2_actionChatInternoMensagens from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoMensagens';
import v2_actionChatInternoDeletarMsg from 'src/actions/v2/chats/chat-interno/v2_actionChatInternoDeletarMsg';

const groupMessagesByDate = (messages) => {
    return messages.reduce((acc, message) => {
        const date = new Date(message.date).toLocaleDateString();
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(message);
        return acc;
    }, {});
};

const ChatMessageItem = ({ msg, user, index, onDelete, onReply, isMiniChat, onOpen }) => {
    return (
        <ChatInternoMessageItem
            isMiniChat={isMiniChat}
            key={index}
            message={msg}
            previous={msg[index - 1]}
            next={msg[index + 1]}
            onOpen={onOpen}
            onDelete={onDelete}
            onReply={onReply}
        />
    );
};

const DateSeparator = ({ date, isMiniChat }) => (
    <div className={`w-full flex justify-center sticky top-1 z-10 ${isMiniChat ? 'mb-1' : 'mb-2'}`}>
        <div className={`
            bg-white/80 dark:bg-[#1A1A1A]/80
            backdrop-blur-md
            border border-gray-200/30 dark:border-[#333]/30
            rounded-full
            ${isMiniChat ? 'px-2.5 py-0.5' : 'px-3.5 py-1'}
        `}>
            <span className={`
                font-semibold uppercase tracking-wider
                text-gray-400 dark:text-[#71717A]
                ${isMiniChat ? 'text-[8px]' : 'text-[10px]'}
            `}>
                {date}
            </span>
        </div>
    </div>
);

const ListMessages = ({
    mensagens,
    user,
    ref,
    onReply,
    onDelete,
    selectedMessage,
    closeReplyMsg,
    isMiniChat,
    onOpen,
}) => {
    const groupedMessages = groupMessagesByDate(mensagens);

    return (
        <div ref={ref} className={`flex flex-col min-h-full mx-auto w-full ${isMiniChat ? 'pb-2' : 'pb-4'}`}>
            <div className="flex-1">
                {Object.keys(groupedMessages).map((date) => (
                    <div key={date} className={`first:mt-0 ${isMiniChat ? 'mt-2' : 'mt-3'}`}>
                        <DateSeparator date={date} isMiniChat={isMiniChat} />
                        <div>
                            {groupedMessages[date].map((msg, index) => (
                                <div key={index} className={isMiniChat ? 'px-1' : 'px-2'}>
                                    <ChatMessageItem
                                        isMiniChat={isMiniChat}
                                        msg={msg}
                                        user={user}
                                        index={index}
                                        onDelete={onDelete}
                                        onReply={onReply}
                                        onOpen={onOpen}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const EmptyState = ({ isMiniChat }) => (
    <div className={`flex flex-col items-center justify-center flex-1 ${isMiniChat ? 'py-6' : 'py-16'}`}>
        <div className={`
            flex items-center justify-center rounded-2xl
            bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-500/10 dark:to-indigo-500/10
            ${isMiniChat ? 'w-10 h-10 mb-2' : 'w-14 h-14 mb-3'}
        `}>
            <Iconify
                icon="solar:chat-round-dots-bold-duotone"
                className={`text-violet-400 dark:text-violet-300 ${isMiniChat ? 'w-5 h-5' : 'w-7 h-7'}`}
            />
        </div>
        <p className={`font-medium text-gray-400 dark:text-[#71717A] ${isMiniChat ? 'text-[10px]' : 'text-sm'}`}>
            Nenhuma mensagem ainda
        </p>
        <p className={`text-gray-300 dark:text-[#555] mt-0.5 ${isMiniChat ? 'text-[9px]' : 'text-xs'}`}>
            Inicie a conversa enviando uma mensagem
        </p>
    </div>
);

const ChatInternoMessagesList = ({ isMiniChat, onReply, onOpen }) => {
    const { user } = useAuth();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const { mensagensStaff, staff } = useSelector((state) => state.staff);
    const [listaMoreMsgs, setListaMoreMsgs] = useState([]);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
    const [messageIdDelete, setMessageIdDelete] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [loadingDeleteMsg, setLoadingDeleteMsg] = useState(null);
    const [showScrollBottom, setShowScrollBottom] = useState(false);

    let msgs = mensagensStaff;

    if (isMiniChat) {
        const chatSession = JSON.parse(sessionStorage.getItem('chatInternoMiniId'));
        if (chatSession) {
            msgs = chatSession?.messages || [];
        }
    }

    const mensagens = [...listaMoreMsgs, ...msgs];
    const chat = useRef(null);
    const [open, setOpen] = useState(null);

    // eslint-disable-next-line no-unused-vars
    let other = { firstName: 'A', lastName: 'A' };
    if (!staff.isGroup && staff.people) {
        staff.people.forEach((person) => {
            if (person._id !== user.id) other = person;
        });
    }

    const handleScroll = useCallback(() => {
        if (!chat.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chat.current;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        setShowScrollBottom(distanceFromBottom > 200);
    }, []);

    const scrollToBottom = useCallback(() => {
        if (chat.current) {
            chat.current.scrollTo({
                top: chat.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, []);

    async function getMoreMsgs() {
        if (!chat.current || loadingMore) return;
        setLoadingMore(true);
        const scrollPosition = chat.current.scrollHeight - chat.current.scrollTop;
        try {
            let querys = `?limit=10&offset=${mensagens?.length}&msgs=1`;
            const result = await v2_actionChatInternoMensagens(staff._id, querys);
            if (result.status === 200) {
                setListaMoreMsgs((prev) => {
                    return [...result.data.data, ...prev];
                });
                setTimeout(() => {
                    if (chat.current) {
                        chat.current.scrollTop = chat.current.scrollHeight - scrollPosition;
                    }
                }, 50);
            }
        } catch (error) {
            console.log(error);
        }
        setLoadingMore(false);
    }

    useEffect(() => {
        if (chat.current) {
            chat.current.scrollTop = chat.current.scrollHeight;
            setTimeout(() => {
                if (chat.current) {
                    chat.current.scrollTop = chat.current.scrollHeight;
                }
            }, 100);
        }
    }, [msgs.length, isMiniChat]);

    async function handleDeleteMsg() {
        setLoading(true);
        try {
            await v2_actionChatInternoDeletarMsg(messageIdDelete);
            setMessageIdDelete(null);
            dispatch(setMessageDeleted(messageIdDelete));
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.log(error);
        }
        setOpenModalConfirmacao(false);
    }

    const hasMessages = mensagens && mensagens.length > 0;

    return (
        <div
            className={`flex-1 min-h-0 overflow-y-auto relative ${isMiniChat
                ? 'scrollbar-none'
                : 'scrollbar-thin scrollbar-thumb-gray-300/60 dark:scrollbar-thumb-[#383838]/60 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-[#565856]'
            }`}
            ref={chat}
            onScroll={handleScroll}
        >
            {/* Subtle pattern background */}
            <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03] pointer-events-none"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
            />

            <div className="relative z-[1]">
                {/* Load more button */}
                <div className={`flex justify-center ${isMiniChat ? 'py-2' : 'py-3'}`}>
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={loadingMore}
                        className={`
                            rounded-full text-gray-400 dark:text-[#71717A]
                            hover:text-violet-600 dark:hover:text-violet-400
                            hover:bg-violet-50 dark:hover:bg-violet-500/10
                            transition-all duration-200 gap-2
                            border border-gray-200/60 dark:border-[#2A2A2A]
                            hover:border-violet-200 dark:hover:border-violet-500/20
                            disabled:opacity-50
                            ${isMiniChat ? 'h-7 px-2.5' : 'h-8 px-4'}
                        `}
                        onClick={getMoreMsgs}
                    >
                        {loadingMore ? (
                            <>
                                <Iconify icon="line-md:loading-loop" className={isMiniChat ? 'w-3 h-3' : 'w-4 h-4'} />
                                <span className={`font-medium ${isMiniChat ? 'text-[10px]' : 'text-xs'}`}>Carregando...</span>
                            </>
                        ) : (
                            <>
                                <Iconify icon="solar:history-line-duotone" className={isMiniChat ? 'w-3 h-3' : 'w-4 h-4'} />
                                <span className={`font-medium ${isMiniChat ? 'text-[10px]' : 'text-xs'}`}>Carregar anteriores</span>
                            </>
                        )}
                    </Button>
                </div>

                <ModalConfimacao
                    open={openModalConfirmacao}
                    onClose={() => {
                        setOpenModalConfirmacao(false);
                        setMessageIdDelete(null);
                    }}
                    onConfirm={handleDeleteMsg}
                    loadingFinalizar={loadingDeleteMsg}
                    mensagem={'Deseja mesmo excluir essa mensagem?'}
                />

                <div>
                    {open && (
                        <ModalLightbox open={open} onClose={() => setOpen(false)} src={`${Config.urlWs || ''}/api/files/${open.content}`} />
                    )}

                    {hasMessages ? (
                        <ListMessages
                            isMiniChat={isMiniChat}
                            selectedMessage={selectedMessage}
                            mensagens={mensagens}
                            user={user}
                            onOpen={setOpen}
                            onReply={onReply}
                            onDelete={(value) => {
                                setOpenModalConfirmacao(true);
                                setMessageIdDelete(value);
                            }}
                        />
                    ) : (
                        <EmptyState isMiniChat={isMiniChat} />
                    )}
                </div>
            </div>

            {/* Scroll to bottom FAB */}
            {showScrollBottom && !isMiniChat && (
                <button
                    onClick={scrollToBottom}
                    className="
                        fixed-bottom-button
                        sticky bottom-4 left-1/2 -translate-x-1/2 z-20
                        w-9 h-9 rounded-full
                        bg-white/90 dark:bg-[#1A1A1A]/90
                        backdrop-blur-md
                        border border-gray-200/60 dark:border-[#383838]/60
                        shadow-lg shadow-black/5 dark:shadow-black/20
                        flex items-center justify-center
                        hover:bg-white dark:hover:bg-[#222222]
                        hover:shadow-xl hover:scale-105
                        active:scale-95
                        transition-all duration-200
                        animate-in fade-in slide-in-from-bottom-2 duration-200
                    "
                >
                    <Iconify icon="solar:arrow-down-linear" className="w-4 h-4 text-gray-500 dark:text-[#ABABAD]" />
                </button>
            )}
        </div>
    );
};

export default ChatInternoMessagesList;
